/**
 * SUNTRONIX ONE — SSO configuration admin.
 *
 * Two protocols, one console.  A Tabs strip lets Ops or a customer
 * Global Admin manage:
 *   • OIDC (Microsoft Entra ID / Azure AD)
 *   • SAML 2.0 (Enterprise IdPs — Entra Enterprise Apps, Okta, Ping,
 *     ADFS, OneLogin, JumpCloud, Google Workspace SAML)
 *
 * Both protocols share the same `tenant_sso_configs` collection —
 * discriminated by ``kind`` — so per-master-company scoping and the
 * "sso_enabled" tenant hint remain consistent across both flows.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  PlusCircle, Pencil, Trash2, ShieldCheck, Copy, Loader2,
  FileText, ExternalLink,
} from "lucide-react";

/* ========================================================================== */
export default function SsoConfigs() {
  const [masters, setMasters] = useState([]);

  const loadMasters = async () => {
    try {
      const { data } = await api.get("/master-companies");
      setMasters(data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { loadMasters(); }, []);

  return (
    <div className="space-y-4 max-w-[1400px]" data-testid="sso-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">
            Administration
          </div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">
            Single sign-on
          </h1>
          <p className="text-[13px] text-slate-500 mt-1 max-w-2xl">
            Wire each customer&apos;s identity provider so their users log in without
            a password.  Choose <b>OIDC</b> for Microsoft Entra tenants, or <b>SAML 2.0</b>{" "}
            for any enterprise IdP (Okta, Ping, ADFS, Google Workspace, etc.).  SUNTRONIX
            never mints a <code className="font-mono text-[11px]">super_admin</code> role
            from a group claim.
          </p>
        </div>
      </div>

      <Tabs defaultValue="oidc" className="w-full" data-testid="sso-tabs">
        <TabsList className="mb-4">
          <TabsTrigger value="oidc" data-testid="sso-tab-oidc">
            <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> OIDC · Entra ID
          </TabsTrigger>
          <TabsTrigger value="saml" data-testid="sso-tab-saml">
            <FileText className="h-3.5 w-3.5 mr-1.5" /> SAML 2.0
          </TabsTrigger>
        </TabsList>

        <TabsContent value="oidc" className="mt-0">
          <OidcSection masters={masters} />
        </TabsContent>

        <TabsContent value="saml" className="mt-0">
          <SamlSection masters={masters} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ========================================================================== */
/*  OIDC section (previous behaviour, unchanged)                                */
/* ========================================================================== */
const EMPTY_OIDC = {
  slug: "", display_name: "", master_company_id: "",
  tenant_id: "", client_id: "", client_secret: "",
  default_role: "customer_viewer", enabled: true,
  allowed_email_domains: "", group_role_map_json: "{}",
};

function OidcSection({ masters }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState({ open: false, initial: null });

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/sso/oidc/configs");
      setRows(data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (r) => {
    if (!window.confirm(`Delete OIDC config "${r.slug}"?`)) return;
    try {
      await api.delete(`/sso/oidc/configs/${r.slug}`);
      toast.success("OIDC config removed"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const copyRedirect = (slug) => {
    const url = `${window.location.origin}/api/sso/oidc/${slug}/callback`;
    navigator.clipboard.writeText(url);
    toast.success("Redirect URI copied — paste into Entra");
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button variant="create" className="bg-slate-900"
                onClick={() => setDialog({ open: true, initial: null })}
                data-testid="sso-add-btn">
          <PlusCircle className="h-4 w-4 mr-1.5" /> Add OIDC config
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-[10px] uppercase text-slate-500">
              <th className="text-left px-3 py-2.5">Slug</th>
              <th className="text-left px-3 py-2.5">Master company</th>
              <th className="text-left px-3 py-2.5">Entra directory</th>
              <th className="text-left px-3 py-2.5">Enabled</th>
              <th className="text-left px-3 py-2.5">Default role</th>
              <th className="text-right px-3 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {busy && rows.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">
                <Loader2 className="h-4 w-4 mx-auto animate-spin" />
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400 text-[13px]">
                No OIDC configurations yet.  Click <b>Add OIDC config</b> to wire the first tenant.
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.slug} data-testid={`sso-row-${r.slug}`} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-mono text-xs">{r.slug}</td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {masters.find((m) => m.id === r.master_company_id)?.name || r.master_company_id}
                </td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-500">
                  {(r.tenant_id || "").slice(0, 8)}…
                </td>
                <td className="px-3 py-2">
                  <StatusPill enabled={r.enabled} />
                </td>
                <td className="px-3 py-2 text-xs capitalize">
                  {(r.default_role || "").replace(/_/g, " ")}
                </td>
                <td className="px-3 py-2 text-right">
                  <RowAction title="Copy redirect URI" testId={`sso-copy-${r.slug}`}
                              onClick={() => copyRedirect(r.slug)}>
                    <Copy className="h-3.5 w-3.5" />
                  </RowAction>
                  <RowAction title="Edit" testId={`sso-edit-${r.slug}`}
                              onClick={() => setDialog({ open: true, initial: r })}>
                    <Pencil className="h-3.5 w-3.5" />
                  </RowAction>
                  <RowAction title="Delete" danger testId={`sso-del-${r.slug}`}
                              onClick={() => remove(r)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </RowAction>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <InfoStrip>
        <b>Setup in Entra:</b> Register an app in your Entra tenant, add the redirect URI
        shown above, generate a client secret, and paste the three values here.  Optional
        group→role mapping accepts <code className="font-mono">group_id: role</code> pairs.
      </InfoStrip>

      <OidcDialog
        open={dialog.open}
        initial={dialog.initial}
        masters={masters}
        onOpenChange={(o) => setDialog({ ...dialog, open: o })}
        onSaved={load}
      />
    </div>
  );
}

function OidcDialog({ open, initial, masters, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY_OIDC);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        slug: initial.slug || "",
        display_name: initial.display_name || "",
        master_company_id: initial.master_company_id || "",
        tenant_id: initial.tenant_id || "",
        client_id: initial.client_id || "",
        client_secret: "",
        default_role: initial.default_role || "customer_viewer",
        enabled: initial.enabled !== false,
        allowed_email_domains: (initial.allowed_email_domains || []).join(", "),
        group_role_map_json: JSON.stringify(initial.group_role_map || {}, null, 2),
      });
    } else {
      setForm(EMPTY_OIDC);
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.slug.match(/^[a-z0-9][a-z0-9-]*$/)) {
      toast.error("Slug must be lowercase letters/digits/hyphens only"); return;
    }
    if (!form.master_company_id) { toast.error("Pick a master company"); return; }
    if (!form.tenant_id || !form.client_id) {
      toast.error("Entra directory ID and client ID are required"); return;
    }
    if (!initial && !form.client_secret) {
      toast.error("Client secret required for new configs"); return;
    }
    let groupMap = {};
    try { groupMap = JSON.parse(form.group_role_map_json || "{}"); }
    catch { toast.error("Group→role map must be valid JSON"); return; }
    const domains = form.allowed_email_domains
      .split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
    setSaving(true);
    try {
      await api.post("/sso/oidc/configs", {
        slug: form.slug,
        display_name: form.display_name || form.slug,
        master_company_id: form.master_company_id,
        tenant_id: form.tenant_id,
        client_id: form.client_id,
        client_secret: form.client_secret || (initial ? "__unchanged__" : ""),
        default_role: form.default_role,
        enabled: form.enabled,
        allowed_email_domains: domains,
        group_role_map: groupMap,
      });
      toast.success(initial ? "OIDC config updated" : "OIDC config created");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
                        data-testid="sso-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? `Edit OIDC · ${initial.slug}` : "Add OIDC configuration"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tenant slug (URL fragment)" testId="sso-slug"
                  value={form.slug} onChange={(v) => set("slug", v.toLowerCase())}
                  disabled={!!initial} placeholder="acmecorp" />
          <Field label="Display name" testId="sso-display"
                  value={form.display_name} onChange={(v) => set("display_name", v)}
                  placeholder="ACME Corp" />
          <MasterSelect masters={masters} value={form.master_company_id}
                         onChange={(v) => set("master_company_id", v)} testId="sso-master" />
          <Field label="Entra directory (tenant_id)" testId="sso-tid"
                  value={form.tenant_id} onChange={(v) => set("tenant_id", v)}
                  placeholder="00000000-0000-0000-0000-000000000000" />
          <Field label="Client ID" testId="sso-cid"
                  value={form.client_id} onChange={(v) => set("client_id", v)}
                  placeholder="00000000-0000-0000-0000-000000000000" />
          <Field label={initial ? "New client secret (leave blank to keep)" : "Client secret"}
                  testId="sso-secret" type="password"
                  value={form.client_secret} onChange={(v) => set("client_secret", v)}
                  placeholder="•••••••••••" />
          <DefaultRoleSelect value={form.default_role}
                              onChange={(v) => set("default_role", v)}
                              testId="sso-role" />
          <div className="col-span-2">
            <Field label="Allowed email domains (comma separated)"
                    testId="sso-domains"
                    value={form.allowed_email_domains}
                    onChange={(v) => set("allowed_email_domains", v)}
                    placeholder="acmecorp.com, acme.co.uk" />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
              Group → role map (JSON)
            </label>
            <Textarea rows={4} value={form.group_role_map_json}
                       onChange={(e) => set("group_role_map_json", e.target.value)}
                       className="font-mono text-[12px] mt-1"
                       data-testid="sso-groups"
                       placeholder='{ "entra-group-guid-1": "customer_global_admin" }' />
          </div>
          <EnabledCheckbox checked={form.enabled}
                            onChange={(v) => set("enabled", v)}
                            testId="sso-enabled" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                    data-testid="sso-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving}
                    className="bg-slate-900" data-testid="sso-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
            {initial ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ========================================================================== */
/*  SAML section                                                                */
/* ========================================================================== */
const EMPTY_SAML = {
  slug: "", display_name: "", master_company_id: "",
  idp_entity_id: "", idp_sso_url: "", idp_x509_cert: "",
  default_role: "customer_viewer", enabled: true,
  allowed_email_domains: "", group_role_map_json: "{}",
};

function SamlSection({ masters }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState({ open: false, initial: null });

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/sso/saml/configs");
      setRows(data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (r) => {
    if (!window.confirm(`Delete SAML config "${r.slug}"?`)) return;
    try {
      await api.delete(`/sso/saml/configs/${r.slug}`);
      toast.success("SAML config removed"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const acsUrl = (slug) => `${window.location.origin}/api/sso/saml/${slug}/acs`;
  const metadataUrl = (slug) => `${window.location.origin}/api/sso/saml/${slug}/metadata`;

  const copy = (url, label) => {
    navigator.clipboard.writeText(url);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button variant="create" className="bg-slate-900"
                onClick={() => setDialog({ open: true, initial: null })}
                data-testid="saml-add-btn">
          <PlusCircle className="h-4 w-4 mr-1.5" /> Add SAML config
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-[10px] uppercase text-slate-500">
              <th className="text-left px-3 py-2.5">Slug</th>
              <th className="text-left px-3 py-2.5">Master company</th>
              <th className="text-left px-3 py-2.5">IdP entity</th>
              <th className="text-left px-3 py-2.5">Enabled</th>
              <th className="text-left px-3 py-2.5">Default role</th>
              <th className="text-right px-3 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {busy && rows.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">
                <Loader2 className="h-4 w-4 mx-auto animate-spin" />
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400 text-[13px]">
                No SAML configurations yet.  Click <b>Add SAML config</b> to onboard an
                enterprise IdP.
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.slug} data-testid={`saml-row-${r.slug}`} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-mono text-xs">{r.slug}</td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {masters.find((m) => m.id === r.master_company_id)?.name || r.master_company_id}
                </td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-500 max-w-[280px] truncate">
                  {r.idp_entity_id || "—"}
                </td>
                <td className="px-3 py-2">
                  <StatusPill enabled={r.enabled} />
                </td>
                <td className="px-3 py-2 text-xs capitalize">
                  {(r.default_role || "").replace(/_/g, " ")}
                </td>
                <td className="px-3 py-2 text-right">
                  <RowAction title="Copy ACS URL" testId={`saml-copy-acs-${r.slug}`}
                              onClick={() => copy(acsUrl(r.slug), "ACS URL")}>
                    <Copy className="h-3.5 w-3.5" />
                  </RowAction>
                  <RowAction title="Open SP metadata"
                              testId={`saml-meta-${r.slug}`}
                              onClick={() => window.open(metadataUrl(r.slug), "_blank")}>
                    <ExternalLink className="h-3.5 w-3.5" />
                  </RowAction>
                  <RowAction title="Edit" testId={`saml-edit-${r.slug}`}
                              onClick={() => setDialog({ open: true, initial: r })}>
                    <Pencil className="h-3.5 w-3.5" />
                  </RowAction>
                  <RowAction title="Delete" danger testId={`saml-del-${r.slug}`}
                              onClick={() => remove(r)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </RowAction>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <InfoStrip>
        <b>Setup in your IdP (Okta, Ping, ADFS, Entra Enterprise Apps, etc.):</b>{" "}
        Create a SAML application, set the <b>ACS (Reply) URL</b> to{" "}
        <code className="font-mono text-[11px]">/api/sso/saml/&lt;slug&gt;/acs</code> and the{" "}
        <b>Audience / Entity ID</b> to the SP metadata URL.  Then paste the IdP&apos;s
        entity ID, SSO URL and x509 signing certificate here.
      </InfoStrip>

      <SamlDialog
        open={dialog.open}
        initial={dialog.initial}
        masters={masters}
        onOpenChange={(o) => setDialog({ ...dialog, open: o })}
        onSaved={load}
      />
    </div>
  );
}

function SamlDialog({ open, initial, masters, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY_SAML);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        slug: initial.slug || "",
        display_name: initial.display_name || "",
        master_company_id: initial.master_company_id || "",
        idp_entity_id: initial.idp_entity_id || "",
        idp_sso_url: initial.idp_sso_url || "",
        idp_x509_cert: "",         // never returned by list endpoint
        default_role: initial.default_role || "customer_viewer",
        enabled: initial.enabled !== false,
        allowed_email_domains: (initial.allowed_email_domains || []).join(", "),
        group_role_map_json: JSON.stringify(initial.group_role_map || {}, null, 2),
      });
    } else {
      setForm(EMPTY_SAML);
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.slug.match(/^[a-z0-9][a-z0-9-]*$/)) {
      toast.error("Slug must be lowercase letters/digits/hyphens only"); return;
    }
    if (!form.master_company_id) { toast.error("Pick a master company"); return; }
    if (!form.idp_entity_id || !form.idp_sso_url) {
      toast.error("IdP entity ID and SSO URL are required"); return;
    }
    if (!initial && !form.idp_x509_cert.trim()) {
      toast.error("IdP x509 signing certificate is required"); return;
    }
    let groupMap = {};
    try { groupMap = JSON.parse(form.group_role_map_json || "{}"); }
    catch { toast.error("Group→role map must be valid JSON"); return; }
    const domains = form.allowed_email_domains
      .split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);

    // Strip PEM header / footer and whitespace — the SAML library
    // accepts either form, but the docs are cleaner with the body only.
    const cert = form.idp_x509_cert
      .replace(/-----BEGIN CERTIFICATE-----/g, "")
      .replace(/-----END CERTIFICATE-----/g, "")
      .replace(/\s+/g, "");

    setSaving(true);
    try {
      await api.post("/sso/saml/configs", {
        slug: form.slug,
        display_name: form.display_name || form.slug,
        master_company_id: form.master_company_id,
        idp_entity_id: form.idp_entity_id,
        idp_sso_url: form.idp_sso_url,
        idp_x509_cert: cert,
        default_role: form.default_role,
        enabled: form.enabled,
        allowed_email_domains: domains,
        group_role_map: groupMap,
      });
      toast.success(initial ? "SAML config updated" : "SAML config created");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
                        data-testid="saml-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? `Edit SAML · ${initial.slug}` : "Add SAML configuration"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tenant slug (URL fragment)" testId="saml-slug"
                  value={form.slug} onChange={(v) => set("slug", v.toLowerCase())}
                  disabled={!!initial} placeholder="acmecorp-saml" />
          <Field label="Display name" testId="saml-display"
                  value={form.display_name} onChange={(v) => set("display_name", v)}
                  placeholder="ACME Corp SAML" />
          <MasterSelect masters={masters} value={form.master_company_id}
                         onChange={(v) => set("master_company_id", v)}
                         testId="saml-master" />
          <Field label="IdP Entity ID (issuer)" testId="saml-entity"
                  value={form.idp_entity_id}
                  onChange={(v) => set("idp_entity_id", v)}
                  placeholder="https://sts.windows.net/{tenant}/" />
          <Field label="IdP SSO URL (redirect)" testId="saml-sso-url"
                  value={form.idp_sso_url}
                  onChange={(v) => set("idp_sso_url", v)}
                  placeholder="https://login.microsoftonline.com/{tenant}/saml2" />
          <DefaultRoleSelect value={form.default_role}
                              onChange={(v) => set("default_role", v)}
                              testId="saml-role" />
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
              IdP x509 signing certificate
              {initial ? " (leave blank to keep current)" : ""}
            </label>
            <Textarea rows={6} value={form.idp_x509_cert}
                       onChange={(e) => set("idp_x509_cert", e.target.value)}
                       className="font-mono text-[11px] mt-1"
                       data-testid="saml-cert"
                       placeholder={"-----BEGIN CERTIFICATE-----\nMIIC…\n-----END CERTIFICATE-----"} />
            <div className="text-[11px] text-slate-500 mt-1">
              Paste the PEM.  Header/footer lines are stripped automatically.
            </div>
          </div>
          <div className="col-span-2">
            <Field label="Allowed email domains (comma separated)"
                    testId="saml-domains"
                    value={form.allowed_email_domains}
                    onChange={(v) => set("allowed_email_domains", v)}
                    placeholder="acmecorp.com, acme.co.uk" />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
              Group → role map (JSON)
            </label>
            <Textarea rows={4} value={form.group_role_map_json}
                       onChange={(e) => set("group_role_map_json", e.target.value)}
                       className="font-mono text-[12px] mt-1"
                       data-testid="saml-groups"
                       placeholder='{ "IT-Admins": "customer_it_admin" }' />
          </div>
          <EnabledCheckbox checked={form.enabled}
                            onChange={(v) => set("enabled", v)}
                            testId="saml-enabled" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                    data-testid="saml-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving}
                    className="bg-slate-900" data-testid="saml-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
            {initial ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ========================================================================== */
/*  Shared UI atoms                                                             */
/* ========================================================================== */
function StatusPill({ enabled }) {
  return (
    <span className={
      "px-2 py-0.5 rounded-full text-[10px] " +
      (enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600")
    }>
      {enabled ? "Enabled" : "Disabled"}
    </span>
  );
}

function RowAction({ children, onClick, title, danger, testId }) {
  return (
    <Button size="sm" variant="ghost"
             className={"h-7 w-7 p-0 " + (danger ? "text-rose-600" : "")}
             title={title} onClick={onClick} data-testid={testId}>
      {children}
    </Button>
  );
}

function InfoStrip({ children }) {
  return (
    <div className="rounded border border-slate-200 bg-slate-50 p-3 flex items-start gap-2
                     text-[12px] text-slate-600 leading-relaxed">
      <ShieldCheck className="h-4 w-4 text-slate-600 mt-0.5 shrink-0" />
      <div>{children}</div>
    </div>
  );
}

function MasterSelect({ masters, value, onChange, testId }) {
  return (
    <div className="col-span-2">
      <label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Master company</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              data-testid={testId}
              className="w-full h-9 border border-slate-200 rounded px-2 text-[13px] mt-1">
        <option value="">— select —</option>
        {masters.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
      </select>
    </div>
  );
}

function DefaultRoleSelect({ value, onChange, testId }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
        Default role (no group match)
      </label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              data-testid={testId}
              className="w-full h-9 border border-slate-200 rounded px-2 text-[13px] mt-1">
        {["customer_viewer", "customer_employee", "customer_manager",
          "customer_it_admin", "customer_global_admin"].map((r) => (
          <option key={r} value={r}>{r.replace(/_/g, " ")}</option>
        ))}
      </select>
    </div>
  );
}

function EnabledCheckbox({ checked, onChange, testId }) {
  return (
    <label className="col-span-2 flex items-center gap-2 text-[13px]">
      <input type="checkbox" checked={checked}
             onChange={(e) => onChange(e.target.checked)}
             data-testid={testId} />
      Enabled — users can log in via this SSO right away
    </label>
  );
}

function Field({ label, value, onChange, testId, type = "text", placeholder, disabled }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</label>
      <Input type={type} value={value} disabled={disabled} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)} data-testid={testId} className="mt-1" />
    </div>
  );
}
